import React, { useContext, useState, useEffect, useReducer } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Body, Box, Card, FilterBar, Filter, Pagination, SearchResultsGrid } from 'dma-ui-kit';
import styled from 'styled-components';
import SiteContext from 'site/SiteContext';
import { translateInterfaceText } from 'helpers/translation';
import { filterQuery, history, unpackFilterQuery } from 'helpers/search';
import ImageNotFound from 'assets/resultmissing.png';
import { reducer as queryReducer } from 'search/queries';
import { fromUnixTime } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { emptyExhibitionResults, fetchExhibitionResults } from 'search/exhibitionSearch';

const Container = styled(Box)`
    padding: 0 12px;

    @media (min-width: 1025px) {
        padding: 0 48px;
    }
    @media (max-width: 1024px) {
        padding: 0 24px;
    }    

    margin-bottom: 10px;
    display: block;
`;

const PaginationContainer = styled(Box)`
    max-width: 320px;
    margin: auto;
    margin-top: 48px;

    @media (min-width: 1025px) {
        max-width: 800px;
    }
    @media (max-width: 1024px) {
        max-width: 640px;
    }    
`;

const ExhibitionSearch = React.memo(() => {
    const { displayLanguage } = useContext(SiteContext);
    const searchEndpoint = `${process.env.REACT_APP_SITESEARCH_BASE_PATH}api/search/exhibition`;

    const noResultsText = translateInterfaceText("No Results Found");
    const loadingText = translateInterfaceText("Loading");

    const { search } = useLocation();
    const params = new URLSearchParams(search);

    const initialTerm = params.get('s');
    const initialFilter = unpackFilterQuery(params.get('f'));
    const initialPage = params.get('p');
    const shortCut = params.get('sh');

    const defaultFilter = { status: ['current'] };

    if (['past', 'upcoming'].includes(shortCut))
      defaultFilter.status = [ shortCut ];

    const labels = {
      location: translateInterfaceText('Location'),
      status: translateInterfaceText('Status')
    };

    const [results, setResults] = useState(emptyExhibitionResults);
    const [loading, setLoading] = useState(false);

    const [options, dispatch] = useReducer(
      queryReducer,
      {  
        type: 'exhibition',
        page: initialPage || 1,
        term: initialTerm || '',
        limit: 9,
        filters: initialFilter || defaultFilter
       },  
    );

     const selectedStatus = options.filters?.status?.[0] || '';
     const selectedLocation = options.filters?.location?.[0] || '';

    useEffect(() => {
      let sectionActive = true;

      const fetchResults = async () => {
        setLoading(true);
        const nextResults = await fetchExhibitionResults(searchEndpoint, options, displayLanguage);

        if (sectionActive) {
          setResults(nextResults);
          setLoading(false);
        }
      };

      fetchResults();

      return () => {
        sectionActive = false;
      };
    }, [searchEndpoint, options, displayLanguage]);
  
    const handleFilterChange = filter => val => {
      const { filters } = options;
      let payload = filters ? { ...filters, [filter]: [val] } : { [filter]: [val] };

      if (filter === 'status') {
        delete payload.location;
      }

      dispatch({ type: 'setFilters', payload });
      dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
      history.push(`/art/exhibitions?s=${options.term}&f=${filterQuery(payload)}&p=1`);
    };

    const handleTermChange = val => {
      dispatch({ type: 'setTerm', payload: val });
      dispatch({ type: 'setPagination', payload: { page: 1, limit: options.limit }});
      history.push(`/art/exhibitions?s=${val}&f=${filterQuery(options.filters)}&p=1`);
    };

    const handlePageChange = val => {
      history.push(`/art/exhibitions?s=${options.term}&f=${filterQuery(options.filters)}&p=${val}`);
      dispatch({ type: 'setPagination', payload: { page: val, limit: options.limit }});
      window.scrollTo(0, 0);
    };

    const formatDateRange = result => {
      if (!result?.field_start_date && !result?.field_end_date) return null;

      const startDate = result.field_start_date
        ? formatInTimeZone(fromUnixTime(result.field_start_date), 'America/Chicago', 'MMM do, yyyy')
        : null;
      const endDate = result.field_end_date
        ? formatInTimeZone(fromUnixTime(result.field_end_date), 'America/Chicago', 'MMM do, yyyy')
        : null;

      if (startDate && endDate) return `${startDate} - ${endDate}`;
      return startDate || endDate;
    };

    return (
        <Container>
          <>
            <Box mb="24px">
              <FilterBar onSearch={handleTermChange} value={options.term}> 
                <Filter
                  key={`status-${selectedStatus}`}
                  options={[
                    { label: 'Any', value: '' },
                    { label: 'Current' , value: 'current' },
                    { label: 'Upcoming' , value: 'upcoming' },
                    { label: 'Past' , value: 'past' }
                  ]}
                  label={labels.status}
                  onChange={handleFilterChange('status')}
                  defaults={{ value: selectedStatus }}
                />
                <Filter
                  key={`location-${selectedStatus}-${selectedLocation}`}
                  options={[
                    { label: 'Any', value: '' },
                    ...(results?.aggregations?.location || []).map(agg => ({ label: agg.key, value: agg.key }))
                  ]}
                  label={labels.location}
                  onChange={handleFilterChange('location')}
                  defaults={{ value: selectedLocation }}
                />
              </FilterBar>
            </Box>
            {results?.hits && results.hits.length > 0 && !loading && (
              <SearchResultsGrid cardsPerRow={3}>
                {results.hits.map((result, index) => {
                    const url = result.field_microsite_link || result.url;
                    const image = result.featured_image || result.thumbnail || ImageNotFound;
                    const caption = formatDateRange(result);

                    return (
                      <Link key={index} to={{ pathname: url }} target={url && url.includes('http') ? '_blank' : null}>
                        <Card
                          variant="calendar"
                          mb="24px"
                          headline={result.field_caption_text}
                          title={result.title}
                          caption={caption ? (<Body variant="small">{caption}</Body>) : null}
                          image={image}
                          alt={result.featured_image_alt || result.thumbnail_alt || result.title}
                        />
                      </Link>
                  );
                })}
              </SearchResultsGrid>
            )}
            {results?.hits?.length === 0 && !loading && (
              <Box w="100%" pt="48px">
                <Body textAlign="center">{noResultsText}</Body>
              </Box>
            )}
            {loading && (
              <Box w="100%" pt="48px">
                <Body textAlign="center">{loadingText}...</Body>
              </Box>
            )}
            <PaginationContainer>
              <Pagination
                currentPage={results?.currentPage || 1}
                pageCount={results?.pageCount || 1}
                onChange={handlePageChange}
              />
            </PaginationContainer>
          </>
        </Container>
      );
});

export default ExhibitionSearch;
